/**
 * useLiveTestingSxSelect
 *
 * Live Testing에서 sx_select 타입 질문을 처리하기 위한 훅
 * V1 QsSxCollector/ModalSxCollector의 V2 버전
 *
 * 기능:
 * - 증상 옵션 클릭 시 해당 트리의 질문들을 모달에서 표시
 * - 모달 내 답변 상태 관리
 * - 증상 선택 상태 관리 (mutuallyExclusive 처리)
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { questionTreeControllerFindByTreeKey } from '../../../../api/generated/question-trees/question-trees';
import { questionControllerFindByTreeId } from '../../../../api/generated/questions/questions';
import { translationControllerSearch } from '../../../../api/generated/translation/translation';
import type { QuestionResponseDto } from '../../../../api/generated/types';
import type { LiveTestingAnswerValue } from './types';
import type { V2QsSelectValue } from '../../../../shared/components/question-types/V2QsSelect';

export interface SxSelectOption {
  /** 옵션 키 (treeKey로도 사용) */
  key: string;
  /** 표시 라벨 */
  label: string;
  /** 선택 여부 */
  checked: boolean;
  /** 상세 답변 있음 여부 */
  hasAnswer: boolean;
  /** 연결된 트리 ID (API 호출용) */
  treeId?: number;
  /** 상호 배타적 옵션 여부 */
  mutuallyExclusive?: boolean;
  /** 노트 필수 여부 */
  requireNote?: boolean;
  /** 비활성화 여부 */
  disabled?: boolean;
}

export interface SxSelectModalState {
  /** 모달 열림 여부 */
  isOpen: boolean;
  /** 현재 선택된 증상 키 */
  selectedSxKey: string | null;
  /** 증상 트리의 질문들 */
  questions: QuestionResponseDto[];
  /** 모달 내 답변 상태 */
  answers: Record<string, LiveTestingAnswerValue>;
  /** 로딩 중 */
  isLoading: boolean;
  /** 에러 메시지 */
  error: string | null;
}

export interface UseLiveTestingSxSelectProps {
  /** 질문의 answerKeys (증상 옵션들의 키) */
  answerKeys: string[];
  /** 질문의 answerOptionsMap */
  answerOptionsMap: Record<string, any> | null;
  /** 현재 선택된 값 */
  value: V2QsSelectValue;
  /** 값 변경 콜백 */
  onChange: (value: V2QsSelectValue) => void;
  /** 현재 로케일 */
  locale: string;
  /** Survey ID (트리 검색용) */
  surveyId?: number;
  /** 증상 트리 내 sx_select 질문 숨김 여부 */
  suppressSxCollectors?: boolean;
}

export interface UseLiveTestingSxSelectReturn {
  /** 옵션 목록 (UI 렌더링용) */
  options: SxSelectOption[];
  /** 모달 상태 */
  modalState: SxSelectModalState;
  /** 옵션 클릭 핸들러 (모달 열기) */
  handleOptionClick: (optionKey: string) => void;
  /** 단순 옵션 체크 핸들러 (트리 없는 옵션) */
  handleSimpleCheck: (optionKey: string, checked: boolean) => void;
  /** 모달 닫기 */
  handleModalClose: () => void;
  /** 모달 저장 */
  handleModalSave: () => void;
  /** 모달 내 답변 업데이트 */
  updateModalAnswer: (questionKey: string, value: LiveTestingAnswerValue) => void;
  /** 삭제 핸들러 */
  handleDelete: (optionKey: string) => void;
  /** 번역 로딩 중 여부 (스켈레톤 표시용) */
  isLoadingOptions: boolean;
}

export function useLiveTestingSxSelect({
  answerKeys,
  answerOptionsMap,
  value,
  onChange,
  locale,
  surveyId,
  suppressSxCollectors = false,
}: UseLiveTestingSxSelectProps): UseLiveTestingSxSelectReturn {
  // 모달 상태
  const [modalState, setModalState] = useState<SxSelectModalState>({
    isOpen: false,
    selectedSxKey: null,
    questions: [],
    answers: {},
    isLoading: false,
    error: null,
  });

  // 증상별 답변 상태 저장 (증상 키 -> 모달 답변들)
  // value에서 itemAnswers 읽기 (확장된 타입으로 저장된 경우)
  const [sxAnswersMap, setSxAnswersMap] = useState<Record<string, Record<string, LiveTestingAnswerValue>>>(() => {
    return (value as any)?.itemAnswers || {};
  });

  // value.itemAnswers가 변경되면 내부 상태 동기화 (화면 이동 후 복귀 시)
  useEffect(() => {
    const valueItemAnswers = (value as any)?.itemAnswers;
    if (valueItemAnswers && Object.keys(valueItemAnswers).length > 0) {
      setSxAnswersMap(valueItemAnswers);
    }
  }, [value]);

  // treeKey -> treeId 매핑 캐시
  const [treeIdCache, setTreeIdCache] = useState<Record<string, number>>({});

  // 번역 캐시 (key -> translated label)
  const [translationCache, setTranslationCache] = useState<Record<string, string>>({});
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const [hasLoadedTranslations, setHasLoadedTranslations] = useState(false);
  const fetchedTransKeysRef = useRef<Set<string>>(new Set());
  const isEnglishLocale = (locale || 'english').toLowerCase() === 'english';

  // answerKeys에 대한 번역 로드
  useEffect(() => {
    if (isEnglishLocale) {
      setHasLoadedTranslations(true);
      return;
    }
    if (!surveyId || answerKeys.length === 0) return;

    const keysToFetch = answerKeys.filter((key) => !translationCache[key] && !fetchedTransKeysRef.current.has(key));
    if (keysToFetch.length === 0) {
      setHasLoadedTranslations(true);
      return;
    }

    keysToFetch.forEach((key) => fetchedTransKeysRef.current.add(key));
    setIsLoadingTranslations(true);

    translationControllerSearch({
      surveyId,
      keys: keysToFetch,
      locale,
      type: 'patient',
      limit: 1000,
    })
      .then((response) => {
        const transResults = (response as any)?.data || (response as any)?.translations || [];
        const newCache: Record<string, string> = {};
        transResults.forEach((t: any) => {
          if (t.key && t.value) {
            newCache[t.key] = t.value;
          }
        });
        if (Object.keys(newCache).length > 0) {
          setTranslationCache((prev) => ({ ...prev, ...newCache }));
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingTranslations(false);
        setHasLoadedTranslations(true);
      });
  }, [answerKeys, surveyId, locale, isEnglishLocale]);

  // 옵션 목록 생성
  const options = useMemo<SxSelectOption[]>(() => {
    const { selectedKeys = [], notesForKeys = {} } = value || {};

    return answerKeys.map((key) => {
      const optionData = answerOptionsMap?.[key];
      const isChecked = selectedKeys.includes(key);
      const hasAnswer = !!sxAnswersMap[key] && Object.keys(sxAnswersMap[key]).length > 0;

      // 라벨 우선순위: 번역 캐시 > fetch 완료 후 fallback > 빈 문자열
      // 비영어 로케일에서 번역이 아직 없으면 빈 문자열 (영어 flash 방지)
      const rawLabel = (optionData?.label as string) || key;
      let label: string;
      if (isEnglishLocale) {
        label = rawLabel;
      } else if (translationCache[key]) {
        label = translationCache[key];
      } else if (fetchedTransKeysRef.current.has(key) && !isLoadingTranslations) {
        // fetch 완료했지만 번역이 없는 키 → 원본 라벨 fallback
        label = rawLabel;
      } else {
        // 아직 fetch 안 됨 or 진행 중 → 빈 문자열로 영어 flash 방지
        label = '';
      }

      return {
        key,
        label,
        checked: isChecked,
        hasAnswer: isChecked && hasAnswer,
        treeId: treeIdCache[key],
        mutuallyExclusive: Boolean(optionData?.mutuallyExclusive),
        requireNote: Boolean(optionData?.requireNote),
        disabled: false,
      };
    });
  }, [answerKeys, answerOptionsMap, value, sxAnswersMap, treeIdCache, translationCache, isLoadingTranslations, isEnglishLocale]);

  // mutuallyExclusive 옵션 키들
  const mutExclusiveKeys = useMemo(() => {
    return options.filter((opt) => opt.mutuallyExclusive).map((opt) => opt.key);
  }, [options]);

  // 옵션 클릭 핸들러 (증상 모달 열기)
  const handleOptionClick = useCallback(
    async (optionKey: string) => {
      setModalState((prev) => ({
        ...prev,
        isOpen: true,
        selectedSxKey: optionKey,
        isLoading: true,
        error: null,
        questions: [],
        answers: sxAnswersMap[optionKey] || {},
      }));

      try {
        // 1. treeKey로 트리 검색하여 treeId 획득
        let treeId = treeIdCache[optionKey];

        if (!treeId && surveyId) {
          // API 호출하여 treeId 찾기 (새 API: 모든 트리 타입 검색)
          const result = await questionTreeControllerFindByTreeKey({
            surveyId,
            treeKey: optionKey,
          });

          if (result) {
            treeId = result.id;
            setTreeIdCache((prev) => ({ ...prev, [optionKey]: treeId! }));
          }
        }

        // 2. treeId로 질문들 로드
        if (treeId) {
          const questions = await questionControllerFindByTreeId(treeId, { locale });

          // suppressSxCollectors가 true면 sx_select 타입 질문 필터링
          const filteredQuestions = suppressSxCollectors
            ? questions.filter((q) => q.inputType !== 'sx_select')
            : questions;

          setModalState((prev) => ({
            ...prev,
            isLoading: false,
            questions: filteredQuestions,
          }));
        } else {
          // 트리가 없으면 단순 선택으로 처리
          setModalState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'No symptom tree found for this option',
          }));
        }
      } catch (error) {
        setModalState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load symptom questions',
        }));
      }
    },
    [sxAnswersMap, treeIdCache, surveyId, locale, suppressSxCollectors]
  );

  // 단순 옵션 체크 핸들러 (트리 없는 옵션)
  const handleSimpleCheck = useCallback(
    (optionKey: string, checked: boolean) => {
      const { selectedKeys = [], notesForKeys = {} } = value || {};
      const isMutExclusive = mutExclusiveKeys.includes(optionKey);

      let newSelectedKeys: string[];

      if (checked && isMutExclusive) {
        // mutuallyExclusive 옵션 선택 시 다른 모든 선택 해제
        newSelectedKeys = [optionKey];
        // 다른 증상들의 답변도 삭제
        setSxAnswersMap({});
      } else if (checked) {
        // 일반 옵션 선택 - mutuallyExclusive 옵션 제거
        newSelectedKeys = [...selectedKeys.filter((k) => !mutExclusiveKeys.includes(k)), optionKey];
      } else {
        // 선택 해제
        newSelectedKeys = selectedKeys.filter((k) => k !== optionKey);
        // 해당 증상의 답변도 삭제
        setSxAnswersMap((prev) => {
          const next = { ...prev };
          delete next[optionKey];
          return next;
        });
      }

      onChange({
        selectedKeys: newSelectedKeys,
        notesForKeys,
      });
    },
    [value, onChange, mutExclusiveKeys]
  );

  // 모달 닫기
  const handleModalClose = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
      selectedSxKey: null,
      questions: [],
      answers: {},
      error: null,
    }));
  }, []);

  // 모달 저장
  const handleModalSave = useCallback(() => {
    const { selectedSxKey, answers } = modalState;
    if (!selectedSxKey) return;

    const { selectedKeys = [], notesForKeys = {} } = value || {};
    const isMutExclusive = mutExclusiveKeys.includes(selectedSxKey);

    // 모달 답변 저장
    const newItemAnswers = {
      ...sxAnswersMap,
      [selectedSxKey]: answers,
    };
    setSxAnswersMap(newItemAnswers);

    // 선택 상태 업데이트
    let newSelectedKeys: string[];

    if (isMutExclusive) {
      newSelectedKeys = [selectedSxKey];
    } else if (selectedKeys.includes(selectedSxKey)) {
      // 이미 선택되어 있으면 유지
      newSelectedKeys = selectedKeys;
    } else {
      // mutuallyExclusive 옵션 제거 후 추가
      newSelectedKeys = [...selectedKeys.filter((k) => !mutExclusiveKeys.includes(k)), selectedSxKey];
    }

    onChange({
      selectedKeys: newSelectedKeys,
      notesForKeys,
      itemAnswers: newItemAnswers,
    } as any);

    handleModalClose();
  }, [modalState, value, onChange, mutExclusiveKeys, handleModalClose, sxAnswersMap]);

  // 모달 내 답변 업데이트
  const updateModalAnswer = useCallback((questionKey: string, answerValue: LiveTestingAnswerValue) => {
    setModalState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionKey]: answerValue,
      },
    }));
  }, []);

  // 삭제 핸들러
  const handleDelete = useCallback(
    (optionKey: string) => {
      const { selectedKeys = [], notesForKeys = {} } = value || {};

      // 선택 해제
      const newSelectedKeys = selectedKeys.filter((k) => k !== optionKey);

      // 답변 삭제
      const newItemAnswers = { ...sxAnswersMap };
      delete newItemAnswers[optionKey];
      setSxAnswersMap(newItemAnswers);

      onChange({
        selectedKeys: newSelectedKeys,
        notesForKeys,
        itemAnswers: newItemAnswers,
      } as any);
    },
    [value, onChange, sxAnswersMap]
  );

  // 번역 로딩 중 여부 (비영어 로케일에서 번역이 아직 로드되지 않은 경우)
  const isLoadingOptions = !isEnglishLocale && !hasLoadedTranslations;

  return {
    options,
    modalState,
    handleOptionClick,
    handleSimpleCheck,
    handleModalClose,
    handleModalSave,
    updateModalAnswer,
    handleDelete,
    isLoadingOptions,
  };
}
